<script lang="ts">
  import { t, getLocale } from '$core/i18n';
import { THEME_IDS, getThemeById } from '$ui/styles/themes.data';
  import type { TranslationKey } from '$core/i18n';
  import { SUPPORTED_LOCALES, type Locale } from '$core/i18n';
  import { untrack } from 'svelte';
  import type {
    PreferencesStore,
    UserPreferences
  } from '$core/preferences';
  import type { Theme } from '$domain/types';
  import {
    SHORTCUTS,
    formatKeyLabel,
    type SessionAction,
    type ShortcutMap
  } from '$features/session';
  import { DEFAULT_SHORTCUTS } from '$features/session/keys';
  import { purgeAllLocalData } from '$core/data/purge';
  import ImportSyllabusModal from '$ui/components/ImportSyllabusModal.svelte';
  import { exportToFile } from '$core/data/export';
  import { importFromFile } from '$core/data/import';
  import { identity, updatePassword } from '$core/identity';
  import FsrsAdvancedPanel from '$ui/components/FsrsAdvancedPanel.svelte';
  import {
    validateDisplayName,
    flushLeaderboardUpload,
    deleteLeaderboardEntry,
    cancelScheduledUpload,
    updateDisplayName,
    getMyLeaderboardEntry
  } from '$features/leaderboard';
  import {
    getPermissionState,
    subscribeToPush,
    unsubscribeFromPush,
    updatePushReminder
  } from '$features/push';
  import {
    uploadAvatar,
    deleteAvatar,
    getAvatarPublicUrl,
    checkAvatarExists
  } from '$features/avatar';
  import { Button } from '$ui/primitives/Button';
  import { Icon } from '$ui/primitives/Icon';
  import { Input } from '$ui/primitives/Input';
  import { Select } from '$ui/primitives/Select';
  import { Checkbox } from '$ui/primitives/Checkbox';
  import { Segmented } from '$ui/primitives/Segmented';

  const COUNTRIES: Array<{ code: string; label: string }> = [
    { code: 'pirate', label: '🏴☠️' },
    { code: 'ar', label: '🇦🇷' },
    { code: 'au', label: '🇦🇺' },
    { code: 'br', label: '🇧🇷' },
    { code: 'ca', label: '🇨🇦' },
    { code: 'cl', label: '🇨🇱' },
    { code: 'co', label: '🇨🇴' },
    { code: 'de', label: '🇩🇪' },
    { code: 'es', label: '🇪🇸' },
    { code: 'fr', label: '🇫🇷' },
    { code: 'gb', label: '🇬🇧' },
    { code: 'in', label: '🇮🇳' },
    { code: 'it', label: '🇮🇹' },
    { code: 'jp', label: '🇯🇵' },
    { code: 'kr', label: '🇰🇷' },
    { code: 'mx', label: '🇲🇽' },
    { code: 'nl', label: '🇳🇱' },
    { code: 'pe', label: '🇵🇪' },
    { code: 'pt', label: '🇵🇹' },
    { code: 'ru', label: '🇷🇺' },
    { code: 'us', label: '🇺🇸' }
  ];

  let { prefs }: { prefs: PreferencesStore } = $props();

  let lbNameError = $state<string | null>(null);
  let lbBusy = $state(false);
  let lbMessage = $state<string | null>(null);
  let lbNameNextChangeAt = $state<string | null>(null);
  let lbOriginalName = $state<string | null>(null);

  function formatCooldownDate(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  }

  let lbLastLoadedUserId: string | null = null;

  $effect(() => {
    const uid = identity.identity.userId;
    if (!identity.isAuthenticated || !uid) return;
    if (lbLastLoadedUserId === uid) return;
    lbLastLoadedUserId = uid;

    void (async () => {
      const mine = await getMyLeaderboardEntry();
      if (mine) {
        lbOriginalName = mine.displayName;
        draft = { ...draft, leaderboardOptIn: true };
        if (
          !draft.leaderboardOptIn ||
          (draft.leaderboardDisplayName ?? '').trim() === (lbOriginalName ?? '')
        ) {
          draft = { ...draft, leaderboardDisplayName: mine.displayName };
        }
      }
    })();
  });

  let pushBusy = $state(false);
  let pushMessage = $state<string | null>(null);
  let pushSupported = $state(true);
  const HOURS = Array.from({ length: 24 }, (_, i) => i);

  let avatarBusy = $state(false);
  let avatarMessage = $state<string | null>(null);
  let avatarVersion = $state(Date.now());
  let hasAvatar = $state(false);
  let avatarInput: HTMLInputElement | undefined = $state();

  function currentAvatarUrl(): string {
    if (!identity.isAuthenticated || !hasAvatar) return '';
    return getAvatarPublicUrl(identity.userId, avatarVersion);
  }

  $effect(() => {
    if (!identity.isAuthenticated || !identity.identity.userId) return;
    void checkAvatarExists(identity.userId).then((exists) => {
      hasAvatar = exists;
    });
  });

  const THEMES: readonly Theme[] = THEME_IDS;

  let draft: UserPreferences = $state(untrack(() => clonePrefs(prefs.prefs)));
  let confirmingDiscard = $state(false);
  let showFsrsAdvanced = $state(false);
  let confirmRef: HTMLDivElement | undefined = $state();

  let dangerKeyword = $state('');
  let dangerTyping = $state(false);

  let keywordToMatch = $derived(
    getLocale() === 'es' ? 'BORRAR TODO' : 'DELETE ALL'
  );

  let keywordOk = $derived(dangerKeyword.trim() === keywordToMatch);

  function startDanger(): void {
    dangerTyping = true;
    dangerKeyword = '';
  }

  function cancelDanger(): void {
    dangerTyping = false;
    dangerKeyword = '';
  }

  function confirmDanger(): void {
    if (!keywordOk) return;
    void onClearAllData();
  }

  let exporting = $state(false);
  let exportMessage = $state<string | null>(null);
  let importMessage = $state<string | null>(null);
  let importFile = $state<File | null>(null);
  let importConfirming = $state(false);
  let importInput: HTMLInputElement | undefined = $state();

  async function doExport() {
    exporting = true;
    exportMessage = null;
    try {
      await exportToFile();
      exportMessage = t('settings.exportDone', { name: 'backup.json', size: '—' });
    } catch (err) {
      console.error('[StudyQuantifier] export failed:', err);
      exportMessage = null;
    } finally {
      exporting = false;
    }
  }

  function pickImportFile() {
    importInput?.click();
  }

  function onImportFileSelected(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    importFile = file;
    importConfirming = true;
    input.value = '';
  }

  function cancelImport() {
    importFile = null;
    importConfirming = false;
  }

  async function confirmImport() {
    if (!importFile) return;
    try {
      await importFromFile(importFile);
      importMessage = t('settings.importDone', { count: 0 });
      importConfirming = false;
      importFile = null;
      setTimeout(() => location.reload(), 1200);
    } catch (err) {
      importMessage = 'Import is not supported yet in the new architecture.';
      importConfirming = false;
      importFile = null;
    }
  }

  function clonePrefs(p: UserPreferences): UserPreferences {
    return { ...p, shortcuts: { ...p.shortcuts } };
  }

  let dirty = $derived(JSON.stringify(draft) !== JSON.stringify(prefs.prefs));

  function captureKey(event: KeyboardEvent, action: SessionAction): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Escape' || event.key === 'Tab') return;
    const key = event.key;
    if (!key) return;
    const shortcuts: ShortcutMap = { ...draft.shortcuts };
    for (const other of Object.keys(shortcuts) as SessionAction[]) {
      if (other !== action && shortcuts[other] === key) shortcuts[other] = '';
    }
    shortcuts[action] = key;
    draft = { ...draft, shortcuts };
    (event.currentTarget as HTMLInputElement).blur();
  }

  // El label del tema viene directo del ThemeDefinition — así añadir un tema
  // nuevo a themes.data.ts lo expone automáticamente en la grilla sin tocar
  // este archivo ni los catálogos i18n.
  function themeLabel(theme: Theme): string {
    return getThemeById(theme).name;
  }

  function localeLabel(locale: Locale): string {
    return locale === 'en' ? t('locale.en') : t('locale.es');
  }

  function resetShortcuts(): void {
    draft = { ...draft, shortcuts: { ...DEFAULT_SHORTCUTS } };
  }

  async function save(): Promise<void> {
    if (draft.leaderboardOptIn) {
      const name = (draft.leaderboardDisplayName ?? '').trim();
      const nameChanged = name.length > 0 && name !== lbOriginalName;

      if (nameChanged) {
        if (!validateName(name)) return;

        if (lbNameNextChangeAt) {
          lbMessage = t('leaderboard.nameCooldown', {
            date: formatCooldownDate(lbNameNextChangeAt)
          });
          revertName();
          return;
        }

        const result = await updateDisplayName(name);
        if (!result.ok) {
          lbNameError = result.message ?? 'Error';
          revertName();
          return;
        }
        lbOriginalName = name;
        lbNameNextChangeAt = new Date(
          Date.now() + 7 * 24 * 3600 * 1000
        ).toISOString();
        await flushLeaderboardUpload({
          userId: identity.userId,
          displayName: name,
          country: draft.leaderboardCountry
        });
      }
    }
    prefs.applyPreferences(draft);
  }

  function cancelDiscard(): void {
    confirmingDiscard = false;
  }

  function confirmDiscard(): void {
    confirmingDiscard = false;
    draft = clonePrefs(prefs.prefs);
  }

  function validateName(value: string): boolean {
    const result = validateDisplayName(value);
    if (result.valid) {
      lbNameError = null;
      return true;
    }
    const map: Record<string, string> = {
      too_short: t('leaderboard.nameTooShort'),
      too_long: t('leaderboard.nameTooLong'),
      invalid_chars: t('leaderboard.nameInvalidChars'),
      reserved: t('leaderboard.nameReserved'),
      offensive: t('leaderboard.nameOffensive')
    };
    lbNameError = result.reason ? map[result.reason] ?? null : null;
    return false;
  }

  function onNameInput(value: string): void {
    draft = { ...draft, leaderboardDisplayName: value };
    if (value.trim().length > 0) validateName(value);
    else lbNameError = null;
  }

  function revertName(): void {
    draft = { ...draft, leaderboardDisplayName: lbOriginalName ?? '' };
    lbNameError = null;
  }

  async function onToggleOptIn(next: boolean): Promise<void> {
    if (lbBusy) return;

    if (!next) {
      draft = { ...draft, leaderboardOptIn: false };
      lbBusy = true;
      try {
        cancelScheduledUpload();
        await deleteLeaderboardEntry();
        lbMessage = t('leaderboard.disabled');
      } finally {
        lbBusy = false;
      }
      return;
    }

    const name = (draft.leaderboardDisplayName ?? '').trim();
    if (name.length === 0 || !validateName(name)) {
      lbMessage = t('leaderboard.optInRequiresName');
      return;
    }

    draft = { ...draft, leaderboardOptIn: true };
    lbBusy = true;
    try {
      await flushLeaderboardUpload({
        userId: identity.userId,
        displayName: name,
        country: draft.leaderboardCountry
      });
      lbMessage = t('leaderboard.saved');
    } finally {
      lbBusy = false;
    }
  }

  let clearing = $state(false);

  async function onClearAllData(): Promise<void> {
    if (clearing) return;
    const ok = window.confirm(t('settings.clearAllDataConfirm'));
    if (!ok) return;
    clearing = true;
    try {
      // purgeAllLocalData() hace location.reload() al final. La sesión de
      // Supabase se preserva vía sq:auth, las preferencias se resetean a
      // DEFAULT_PREFERENCES (theme = wine), y el sync no encuentra nada
      // remoto para traer de vuelta.
      const result = await purgeAllLocalData();
      if (result.remoteErrors.length > 0) {
        console.warn(
          `[purge] ${result.remoteErrors.length} remote tables failed to delete`,
          result.remoteErrors
        );
      }
    } finally {
      clearing = false;
    }
  }

  function onCountryChange(code: string): void {
    draft = { ...draft, leaderboardCountry: code };
    if (draft.leaderboardOptIn) {
      const name = (draft.leaderboardDisplayName ?? '').trim();
      if (name.length > 0) {
        void flushLeaderboardUpload({
          userId: identity.userId,
          displayName: name,
          country: code
        });
      }
    }
  }

  function currentTimezoneOffset(): number {
    return new Date().getTimezoneOffset();
  }

  async function onTogglePush(next: boolean): Promise<void> {
    if (pushBusy) return;
    pushBusy = true;
    pushMessage = null;
    try {
      if (next) {
        const result = await subscribeToPush({
          reminderHour: draft.pushReminderHour,
          timezoneOffsetMinutes: currentTimezoneOffset()
        });
        if (result.ok) {
          draft = { ...draft, pushEnabled: true };
          pushMessage = t('push.enabled');
        } else if (result.reason === 'permission_denied') {
          pushMessage = t('push.permissionDenied');
        } else if (result.reason === 'unsupported') {
          pushMessage = t('push.unsupported');
        } else {
          pushMessage = t('push.failed');
        }
      } else {
        await unsubscribeFromPush();
        draft = { ...draft, pushEnabled: false };
        pushMessage = t('push.disabled');
      }
    } finally {
      pushBusy = false;
    }
  }

  async function onReminderHourChange(hour: number): Promise<void> {
    draft = { ...draft, pushReminderHour: hour };
    if (draft.pushEnabled) {
      const ok = await updatePushReminder(hour, currentTimezoneOffset());
      if (ok) pushMessage = t('push.saved');
    }
  }

  $effect(() => {
    pushSupported = getPermissionState() !== 'unsupported';
  });



  $effect(() => {
    if (confirmingDiscard && confirmRef) {
      confirmRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  function pickAvatar(): void {
    avatarInput?.click();
  }

  async function onAvatarSelected(event: Event): Promise<void> {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!identity.isAuthenticated) return;
    avatarBusy = true;
    avatarMessage = null;
    try {
      const result = await uploadAvatar(identity.userId, file);
      if (result.ok) {
        avatarVersion = Date.now();
        hasAvatar = true;
        avatarMessage = t('avatar.uploaded');
        await syncAvatarToLeaderboard(currentAvatarUrl());
      } else if (result.reason === 'too_large') {
        avatarMessage = t('avatar.tooLarge');
      } else if (result.reason === 'invalid_type') {
        avatarMessage = t('avatar.invalidType');
      } else {
        avatarMessage = t('avatar.failed');
      }
    } finally {
      avatarBusy = false;
    }
  }

  async function onRemoveAvatar(): Promise<void> {
    if (!identity.isAuthenticated) return;
    avatarBusy = true;
    avatarMessage = null;
    try {
      await deleteAvatar(identity.userId);
      avatarVersion = Date.now();
      hasAvatar = false;
      avatarMessage = t('avatar.removed');
      await syncAvatarToLeaderboard(null);
    } finally {
      avatarBusy = false;
    }
  }

  let activeTab = $state<'appearance' | 'shortcuts' | 'session' | 'fsrs' | 'profile' | 'data'>('appearance');

  let newPassword = $state('');
  let passwordBusy = $state(false);
  let passwordMessage = $state<string | null>(null);

  async function handleUpdatePassword() {
    if (newPassword.length < 6) return;
    passwordBusy = true;
    passwordMessage = null;
    try {
      await updatePassword(newPassword);
      passwordMessage = 'Password updated successfully!';
      newPassword = '';
    } catch (e) {
      passwordMessage = e instanceof Error ? e.message : 'Failed to update password.';
    } finally {
      passwordBusy = false;
    }
  }

  async function syncAvatarToLeaderboard(
    avatarUrl: string | null
  ): Promise<void> {
    if (!draft.leaderboardOptIn) return;
    const name = (draft.leaderboardDisplayName ?? '').trim();
    if (name.length === 0) return;
    await flushLeaderboardUpload({
      userId: identity.userId,
      displayName: name,
      country: draft.leaderboardCountry,
      avatarUrl
    });
  }
</script>

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('settings.title')}</h1>
  </header>

  <div class="settings-layout">
    <!-- SUB-NAV INTERNA -->
    <nav class="settings-nav" aria-label="Settings tabs">
      <button type="button" class="nav-item" class:active={activeTab === 'appearance'} onclick={() => (activeTab = 'appearance')}>
        <Icon name="palette" size={16} /> Apariencia
      </button>
      <button type="button" class="nav-item" class:active={activeTab === 'session'} onclick={() => (activeTab = 'session')}>
        <Icon name="timer" size={16} /> Sesión
      </button>
      <button type="button" class="nav-item" class:active={activeTab === 'shortcuts'} onclick={() => (activeTab = 'shortcuts')}>
        <Icon name="keyboard" size={16} /> Atajos
      </button>
      <button type="button" class="nav-item" class:active={activeTab === 'fsrs'} onclick={() => (activeTab = 'fsrs')}>
        <Icon name="activity" size={16} /> FSRS
      </button>
      <button type="button" class="nav-item" class:active={activeTab === 'profile'} onclick={() => (activeTab = 'profile')}>
        <Icon name="user" size={16} /> Perfil
      </button>
      <button type="button" class="nav-item" class:active={activeTab === 'data'} onclick={() => (activeTab = 'data')}>
        <Icon name="database" size={16} /> Datos
      </button>
    </nav>

    <!-- ÁREA DE CONTENIDO -->
    <div class="settings-content">
      {#if activeTab === 'appearance'}
        <div class="panel fade-in">
          <div class="panel-section">
            <h3 class="panel-title">{t('settings.language')}</h3>
            <p class="hint">{t('settings.languageHint')}</p>
            <Segmented
              bind:value={draft.locale}
              options={SUPPORTED_LOCALES.map((loc) => ({ value: loc, label: localeLabel(loc) }))}
              ariaLabel={t('settings.language')}
            />
          </div>
          <div class="panel-section">
            <h3 class="panel-title">{t('settings.theme')}</h3>
            <p class="hint">{t('settings.customizeTheme')}</p>
            <div class="theme-list" role="radiogroup" aria-label={t('settings.theme')}>
              {#each THEMES as th (th)}
                {@const def = getThemeById(th)}
                <button
                  type="button"
                  class="theme-row"
                  class:active={draft.theme === th}
                  onclick={() => (draft = { ...draft, theme: th })}
                  role="radio"
                  aria-checked={draft.theme === th}
                  aria-label={themeLabel(th)}
                >
                  <div class="theme-dots">
                    <span class="dot" style="background: {def.tokens['color-brand']};"></span>
                    <span class="dot" style="background: {def.tokens['color-success']};"></span>
                    <span class="dot" style="background: {def.tokens['color-danger']};"></span>
                    <span class="dot" style="background: {def.tokens['color-info']};"></span>
                    <span class="dot" style="background: {def.tokens['color-warning']};"></span>
                  </div>
                  <span class="theme-row-name">{themeLabel(th)}</span>
                  {#if draft.theme === th}
                    <span class="theme-check-row" aria-hidden="true">✓</span>
                  {/if}
                </button>
              {/each}
            </div>
          </div>
        </div>
      {:else if activeTab === 'session'}
        <div class="panel fade-in">
          <div class="panel-section">
            <h3 class="panel-title">Feedback de Sesión</h3>
            <p class="hint">Selecciona qué información quieres registrar al final de cada sesión.</p>
            <div class="checkbox-group">
              <Checkbox bind:checked={draft.showSessionContextBar} label="Show Session Context Bar (Subject > Subtopic...)" />
              <Checkbox bind:checked={draft.showFrustration} label={t('settings.showFrustration')} />
              <Checkbox bind:checked={draft.showEnergy} label={t('settings.showEnergy')} />
              <Checkbox bind:checked={draft.showReadingTime} label={t('settings.showReadingTime')} />
              <Checkbox bind:checked={draft.showDistractions} label={t('settings.showDistractions')} />
              <Checkbox bind:checked={draft.alarmEnabled} label={t('settings.alarmEnabled')} />
              <Checkbox bind:checked={draft.keepScreenAwake} label={t('settings.keepScreenAwake')} />
            </div>
          </div>
        </div>
      {:else if activeTab === 'shortcuts'}
        <div class="panel fade-in">
          <div class="panel-header">
            <div>
              <h3 class="panel-title">{t('settings.shortcuts')}</h3>
              <p class="hint">{t('settings.shortcutsHint')}</p>
            </div>
            <Button variant="plain" size="sm" onclick={resetShortcuts}>
              {t('settings.resetShortcuts')}
            </Button>
          </div>
          <div class="shortcuts-list">
            {#each SHORTCUTS as spec (spec.action)}
              <div class="shortcut-row">
                <span class="shortcut-label">{t(spec.labelKey as TranslationKey)}</span>
                <input
                  class="shortcut-key"
                  readonly
                  value={draft.shortcuts[spec.action] ? formatKeyLabel(draft.shortcuts[spec.action]) : t('shortcuts.none')}
                  onkeydown={(e) => captureKey(e, spec.action)}
                  onfocus={(e) => (e.currentTarget as HTMLInputElement).select()}
                  aria-label={t(spec.labelKey as TranslationKey)}
                />
              </div>
            {/each}
          </div>
        </div>
      {:else if activeTab === 'fsrs'}
        <div class="panel fade-in">
          <div class="panel-section">
            <h3 class="panel-title">{t('fsrsAdvanced.title')}</h3>
            <p class="hint">{t('fsrsAdvanced.globalHint')}</p>
            <Button variant="outline" onclick={() => (showFsrsAdvanced = true)}>
              {t('fsrsAdvanced.title')}
            </Button>
          </div>
          <div class="panel-section">
            <h3 class="panel-title">{t('push.sectionTitle')}</h3>
            <p class="hint">{t('push.sectionHint')}</p>
            {#if !pushSupported}
              <p class="info error-text">{t('push.unsupported')}</p>
            {:else}
              <Checkbox
                bind:checked={draft.pushEnabled}
                disabled={pushBusy}
                label={t('push.toggle')}
                onchange={(v) => void onTogglePush(v)}
              />
              {#if draft.pushEnabled}
                <Select
                  label={t('push.reminderHour')}
                  value={String(draft.pushReminderHour)}
                  options={HOURS.map((h) => ({ value: String(h), label: `${String(h).padStart(2, '0')}${t('push.hourSuffix')}` }))}
                  onchange={(v) => void onReminderHourChange(Number(v))}
                />
              {/if}
            {/if}
            {#if pushMessage}
              <p class="info">{pushMessage}</p>
            {/if}
          </div>
        </div>
      {:else if activeTab === 'profile'}
        <div class="panel fade-in">
          <div class="panel-section">
            <h3 class="panel-title">Security</h3>
            <p class="hint">Update your account password here.</p>
            <div class="row-actions" style="align-items: flex-end;">
              <div style="flex: 1; max-width: 300px;">
                <Input 
                  type="password" 
                  label="New Password" 
                  placeholder="Enter new password..." 
                  bind:value={newPassword} 
                />
              </div>
              <Button 
                variant="filled" 
                disabled={newPassword.length < 6 || passwordBusy} 
                loading={passwordBusy} 
                onclick={handleUpdatePassword}
              >
                Update Password
              </Button>
            </div>
            {#if passwordMessage}
              <p class="info" style="color: {passwordMessage.includes('success') ? 'var(--color-success)' : 'var(--color-danger)'}">
                {passwordMessage}
              </p>
            {/if}
          </div>

          <div class="panel-section">
            <h3 class="panel-title">{t('avatar.sectionTitle')}</h3>
            <p class="hint">{t('avatar.sectionHint')}</p>
            <div class="avatar-row">
              <div class="avatar-preview">
                {#if hasAvatar && currentAvatarUrl()}
                  <img src={currentAvatarUrl()} alt="" width="64" height="64" onerror={() => { hasAvatar = false; }} />
                {:else}
                  <span class="avatar-placeholder">?</span>
                {/if}
              </div>
              <div class="row-actions">
                <Button variant="filled" disabled={avatarBusy || !identity.isAuthenticated} loading={avatarBusy} onclick={pickAvatar}>
                  {t('avatar.upload')}
                </Button>
                {#if hasAvatar}
                  <Button variant="outline" disabled={avatarBusy || !identity.isAuthenticated} onclick={() => void onRemoveAvatar()}>
                    {t('avatar.remove')}
                  </Button>
                {/if}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" bind:this={avatarInput} onchange={(e) => void onAvatarSelected(e)} style="display:none" />
              </div>
            </div>
            {#if avatarMessage}
              <p class="info">{avatarMessage}</p>
            {/if}
          </div>
          <div class="panel-section">
            <h3 class="panel-title">{t('leaderboard.sectionTitle')}</h3>
            <p class="hint">{t('leaderboard.sectionHint')}</p>
            <Checkbox bind:checked={draft.leaderboardOptIn} disabled={lbBusy} label={t('leaderboard.optIn')} onchange={(v) => void onToggleOptIn(v)} />
            <Input
              type="text" maxlength={24} label={t('leaderboard.displayName')} placeholder={t('leaderboard.displayNamePlaceholder')}
              value={draft.leaderboardDisplayName ?? ''} error={lbNameError ?? undefined}
              helper={draft.leaderboardOptIn && lbNameNextChangeAt && !lbNameError ? t('leaderboard.nameCooldownShort', { date: formatCooldownDate(lbNameNextChangeAt) }) : undefined}
              oninput={(e) => onNameInput((e.currentTarget as HTMLInputElement).value)}
            />
            {#if lbMessage && !lbNameError}
              <p class="info info-text">{lbMessage}</p>
            {/if}
            <Select
              label={t('leaderboard.country')} value={draft.leaderboardCountry}
              options={COUNTRIES.map((c) => ({ value: c.code, label: `${c.label} ${c.code === 'pirate' ? t('leaderboard.countryPirate') : c.code.toUpperCase()}` }))}
              helper={t('leaderboard.countryHint')} onchange={(v) => v && onCountryChange(v)}
            />
          </div>
        </div>
      {:else if activeTab === 'data'}
        <div class="panel fade-in">
          <div class="panel-section import-panel-wrapper">
            <ImportSyllabusModal onImportSuccess={() => {}} />
          </div>

          <div class="panel-section">
            <h3 class="panel-title">{t('settings.backup')}</h3>
            <p class="hint">{t('settings.backupHint')}</p>
            <div class="row-actions">
              <Button variant="filled" disabled={exporting} loading={exporting} onclick={() => void doExport()}>
                {exporting ? t('settings.exporting') : t('settings.export')}
              </Button>
              <Button variant="outline" onclick={pickImportFile}>{t('settings.import')}</Button>
              <input type="file" accept=".json,.gz,.json.gz,application/gzip,application/json" bind:this={importInput} onchange={onImportFileSelected} style="display: none" />
            </div>
            {#if exportMessage}<p class="info">{exportMessage}</p>{/if}
            {#if importMessage}<p class="info">{importMessage}</p>{/if}
            {#if importConfirming}
              <div class="confirm">
                <p class="warn">{t('settings.importConfirm')}</p>
                <div class="row-actions">
                  <Button variant="danger" onclick={() => void confirmImport()}>{t('settings.importYes')}</Button>
                  <Button variant="plain" onclick={cancelImport}>{t('settings.importCancel')}</Button>
                </div>
              </div>
            {/if}
          </div>
          <div class="panel-section danger-zone">
            <h3 class="panel-title danger-title">{t('settings.dangerZone')}</h3>
            <p class="hint">{t('settings.dangerHint')}</p>
            {#if !dangerTyping}
              <div class="danger-item">
                <div class="danger-texts">
                  <span class="danger-item-title">{t('settings.dangerPurgeLabel')}</span>
                  <span class="danger-description">{t('settings.dangerPurgeDescription')}</span>
                </div>
                <Button variant="danger" onclick={startDanger}>{t('settings.dangerPurge')}</Button>
              </div>
            {:else}
              <div class="confirm danger-confirm">
                <p class="warn">{t('settings.dangerKeywordInstruction', { keyword: keywordToMatch })}</p>
                <Input type="text" autocomplete="off" spellcheck={false} placeholder={t('settings.dangerKeywordPlaceholder')} bind:value={dangerKeyword} />
                <div class="row-actions">
                  <Button variant="danger" disabled={!keywordOk} onclick={confirmDanger}>{t('settings.dangerPurge')}</Button>
                  <Button variant="plain" onclick={cancelDanger}>{t('settings.cancel')}</Button>
                </div>
              </div>
            {/if}
          </div>
        </div>
      {/if}

      <!-- FOOTER DE GUARDADO -->
      <footer class="view-foot">
        {#if confirmingDiscard}
          <div class="confirm" bind:this={confirmRef}>
            <p class="warn">{t('settings.unsavedWarning')}</p>
            <div class="row-actions">
              <Button variant="danger" onclick={confirmDiscard}>{t('settings.confirmDiscard')}</Button>
              <Button variant="plain" onclick={cancelDiscard}>{t('settings.cancel')}</Button>
            </div>
          </div>
        {:else}
          <div class="row-actions end">
            {#if dirty}
              <Button variant="outline" onclick={() => (confirmingDiscard = true)}>{t('settings.discard')}</Button>
            {/if}
            <Button variant="filled" onclick={() => void save()}>{t('settings.save')}</Button>
          </div>
        {/if}
      </footer>
    </div>
  </div>

  {#if showFsrsAdvanced}
    <FsrsAdvancedPanel onClose={() => (showFsrsAdvanced = false)} />
  {/if}


</section>



<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    max-width: 1000px;
    width: 100%;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
  }

  .checkbox-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-top: var(--space-2);
  }

  .checkbox-group :global(label) {
    text-transform: none;
    letter-spacing: normal;
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .settings-layout {
    display: flex;
    gap: var(--space-6);
    align-items: flex-start;
  }

  .settings-nav {
    width: 220px;
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    color: var(--color-text-muted);
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    text-align: left;
    cursor: pointer;
    transition: all var(--dur-fast) var(--ease-out);
  }

  .nav-item:hover {
    color: var(--color-text-primary);
    background: var(--color-surface-hover);
  }

  .nav-item.active {
    color: var(--color-brand);
    background: color-mix(in srgb, var(--color-brand) 10%, transparent);
    border-color: color-mix(in srgb, var(--color-brand) 30%, transparent);
    font-weight: 600;
  }

  .settings-content {
    flex: 1;
    min-width: 0;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-xl);
    display: flex;
    flex-direction: column;
    min-height: 500px;
  }

  .panel {
    padding: var(--space-6);
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    flex: 1;
  }

  .panel-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .import-panel-wrapper {
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
  }

  .import-panel-wrapper :global(.import-panel) {
    margin-top: 0;
    padding: 0;
    background: transparent;
    border: none;
  }

  .panel-title {
    margin: 0;
    font-size: var(--text-h3);
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-4);
  }



  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .view-foot {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--space-4);
    border-top: var(--border-width) solid var(--color-border);
  }







  /* Theme List */
  .theme-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-2);
  }

  @media (max-width: 640px) {
    .theme-list {
      grid-template-columns: 1fr;
    }
  }

  .theme-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    cursor: pointer;
    text-align: left;
    transition: all var(--dur-fast) var(--ease-out);
  }

  .theme-row:hover {
    border-color: var(--color-border-strong);
    background: var(--color-surface-hover);
  }

  .theme-row.active {
    border-color: var(--color-brand);
    background: color-mix(in srgb, var(--color-brand) 5%, transparent);
  }

  .theme-dots {
    display: flex;
    gap: 4px;
    flex: 0 0 auto;
  }

  .theme-dots .dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
  }

  .theme-row-name {
    flex: 1;
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .theme-check-row {
    color: var(--color-brand);
    font-weight: 700;
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .shortcuts-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .shortcut-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .shortcut-label {
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }

  .shortcut-key {
    width: 96px;
    padding: var(--space-1) var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    text-align: center;
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border-strong);
    border-radius: var(--radius-sm);
    cursor: pointer;
    outline: none;
    transition: border-color var(--dur-fast) var(--ease-out);
  }

  .shortcut-key:focus-visible {
    border-color: var(--color-accent);
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: -2px;
  }

  .row-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .row-actions.end {
    justify-content: flex-end;
  }

  .info {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-success);
    font-family: var(--font-mono);
    overflow-wrap: anywhere;
  }

  .info-text {
    color: var(--color-accent);
  }

  .avatar-row {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }

  .avatar-preview {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex: 0 0 auto;
  }

  .avatar-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-placeholder {
    font-size: 1.5rem;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }

  .confirm {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-3);
    border: var(--border-width) solid var(--color-danger);
    border-radius: var(--radius-md);
    background: var(--color-surface-sunken);
    width: 100%;
  }

  .warn {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }

  .danger-zone {
    padding-bottom: 0;
  }

  .danger-title {
    color: var(--color-danger);
  }

  .danger-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4);
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    border-left: 3px solid var(--color-danger);
    border-radius: var(--radius-md);
  }

  .danger-texts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .danger-item-title {
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
  }

  .danger-description {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    overflow-wrap: anywhere;
  }

  .danger-confirm {
    width: 100%;
  }
  /* === RESPONSIVE MÓVIL PARA PREFERENCIAS === */
  @media (max-width: 768px) {
    .settings-layout {
      flex-direction: column;
    }
    
    /* El menú lateral se convierte en una barra horizontal con scroll */
    .settings-nav {
      width: 100%;
      flex-direction: row;
      overflow-x: auto;
      padding-bottom: var(--space-2);
      border-bottom: 1px solid var(--color-border);
      scrollbar-width: none; /* Ocultar barra de scroll */
    }
    .settings-nav::-webkit-scrollbar {
      display: none;
    }
    
    .nav-item {
      white-space: nowrap;
      flex-shrink: 0;
    }
    
    .settings-content {
      width: 100%;
      min-width: 0;
      /* Quitar el fondo y borde de tarjeta en móvil para ganar espacio */
      background: transparent;
      border: none;
    }
  }
</style>